import React, { useEffect, useRef } from "react";
import type { SponsoredAdSlot } from "../../../../Redux/ecommersProduct/types";
import { useAppSelector } from "../../../../Redux/hooks";

// Ad_Service's own NestJS app mounts its global prefix as REACT_APP_ADVERTISEMENTS_API_PERFIX
// (e.g. /api/v1/advertisement) — every route already lives under that path, so callers
// must not also append "/api/v1" themselves.
const AD_SERVICE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ADVERTISEMENTS_API_PERFIX,
]
  .filter(Boolean)
  .join("");

const SESSION_KEY = "salar_ad_session_id";
const PLACEHOLDER_IMAGE = "/assets/img/salarlogo.png";

// A few older campaigns still have the seller's raw login email stored as
// advertiserName (backend bug, fixed at the source, but existing data is unaffected) —
// never render a value that looks like an email on a public storefront page.
const isEmailLike = (s?: string): boolean => !!s && /\S+@\S+\.\S+/.test(s);

const getSessionId = (): string => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

type StorePage = {
  pageTitle: string;
  pageUrl: string;
  imageUrl?: string;
};

type FeaturedProduct = {
  productId: string;
  name: string;
  image?: string;
  catalogProductId?: string;
};

type Props = {
  slot: SponsoredAdSlot;
};

const isValidImageUrl = (url?: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url);

const resolveImage = (url?: string) =>
  isValidImageUrl(url) ? url : PLACEHOLDER_IMAGE;

// Sponsored Brands: product_collection, store_spotlight, or video creatives.
const SponsoredBrandSection: React.FC<Props> = ({ slot }) => {
  const ref = useRef<HTMLDivElement>(null);
  const fired = useRef(false);
  // Attribution (linking a later order back to this click/impression) is keyed by
  // userId when logged in, falling back to sessionId — Ad_Service's budget/attribution
  // service already reads dto.userId, this was just never sent.
  const userId = useAppSelector((state) => state.customerAuth.user?._id);

  useEffect(() => {
    if (!ref.current || fired.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          fetch(`${AD_SERVICE_URL}/ads/impression`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              impressionId: slot.impressionId ?? "",
              trackingToken: slot.trackingToken,
              sessionId: getSessionId(),
              userId,
            }),
          }).catch(() => null);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [slot.trackingToken, slot.impressionId, userId]);

  const fireClick = async (e: React.MouseEvent, targetUrl?: string) => {
    e.preventDefault();
    let redirect = targetUrl;
    try {
      const res = await fetch(`${AD_SERVICE_URL}/ads/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trackingToken: slot.trackingToken,
          sessionId: getSessionId(),
          userId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!redirect && typeof data?.redirectUrl === "string") {
          redirect = data.redirectUrl;
        }
      }
    } catch {}
    if (redirect) window.location.href = redirect;
  };

  const creative = slot.creative;
  const format = creative?.creativeFormat ?? "product_collection";
  const logo = creative?.logoUrl;
  const safeAdvertiserName = isEmailLike(slot.advertiserName) ? undefined : slot.advertiserName;
  const headline = creative?.headline || safeAdvertiserName;
  const brandName = creative?.brandName || safeAdvertiserName;
  const customImage = creative?.customImageUrl;
  const storePages = (creative?.storePages ?? []) as StorePage[];
  const products = (creative?.featuredProducts ?? []) as FeaturedProduct[];
  const videoUrl = creative?.videoUrl;
  const thumbnailUrl = creative?.thumbnailUrl;

  const hasStoreSpotlight = format === "store_spotlight" && storePages.length > 0;
  const hasProducts = products.length > 0;
  const hasVideo = format === "video" && !!videoUrl;
  const hasHeroOnly =
    !hasStoreSpotlight && !hasProducts && !hasVideo && !!customImage;

  if (!hasStoreSpotlight && !hasProducts && !hasVideo && !hasHeroOnly) {
    return null;
  }

  const defaultLanding =
    storePages[0]?.pageUrl ||
    (hasProducts
      ? `/DetailPage/${products[0].catalogProductId || products[0].productId}`
      : undefined);

  const sponsoredLabel = (
    <div className="d-flex justify-content-end align-items-end">
      <b className="text-black-50 fw-bolder text-xs">
        Sponsored <i className="bi bi-info-circle-fill"></i>
      </b>
    </div>
  );

  const brandHeader = (
    <div className="d-flex justify-content-start align-items-start gap-2">
      {logo && isValidImageUrl(logo) && (
        <div className="border-end px-4">
          <img
            src={logo}
            alt={brandName}
            className="w-100 object-fit-contain"
            height={50}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}
      <div className="lh-sm">
        <b className="text-black fw-bold text-2xl">{headline}</b>
        {brandName && (
          <div>
            <a href="#" onClick={(e) => fireClick(e, defaultLanding)}>
              <b className="text-black fw-normal text-xs">Shop {brandName}</b>
            </a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div ref={ref} className="display-max">
      {brandHeader}

      {/* Store spotlight — 3 store sub-pages */}
      {hasStoreSpotlight && (
        <div className="card">
          <div className="d-flex justify-content-start align-items-center mb-4 mt-4 gap-3">
            {storePages.slice(0, 3).map((page, idx) => (
              <div className="w-70" key={`${page.pageUrl}-${idx}`}>
                <div className="cat-pro-width card border-radius-0 shadow-none mb-lg-0">
                  <a href={page.pageUrl} onClick={(e) => fireClick(e, page.pageUrl)}>
                    <div className="card-header p-0 position-relative border-radius-lg z-index-2 bg-gray-100">
                      <div className="d-block blur-shadow-image">
                        <img
                          src={resolveImage(page.imageUrl)}
                          alt={page.pageTitle}
                          className="img-fluid-cat-pro p-2"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      </div>
                    </div>
                  </a>
                  <div className="p-2 text-start lh-0">
                    <b className="mt-2 fw-normal text-xxs text-black">{page.pageTitle}</b>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product collection — hero image + up to 3 featured product tiles */}
      {hasProducts && (
        <div className="card">
          <div className="d-flex justify-content-start align-items-center mb-4 mt-4 gap-3">
            {isValidImageUrl(customImage) && (
              <div
                className="w-100"
                style={{ cursor: "pointer" }}
                onClick={(e) => fireClick(e, defaultLanding)}
              >
                <img
                  src={customImage}
                  alt={headline}
                  className="w-100 object-fit-contain"
                  height={160}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            )}
            {products.slice(0, 3).map((p) => {
              const url = p.catalogProductId
                ? `/DetailPage/${p.catalogProductId}`
                : `/DetailPage/${p.productId}`;
              return (
                <div className="w-70" key={p.productId}>
                  <div className="cat-pro-width card border-radius-0 shadow-none mb-lg-0">
                    <a href={url} onClick={(e) => fireClick(e, url)}>
                      <div className="card-header p-0 position-relative border-radius-lg z-index-2 bg-gray-100">
                        <div className="d-block blur-shadow-image">
                          <img
                            src={resolveImage(p.image)}
                            alt={p.name}
                            className="img-fluid-cat-pro p-2"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src = PLACEHOLDER_IMAGE;
                            }}
                          />
                        </div>
                      </div>
                    </a>
                    <div className="p-2 text-start lh-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <b className="font-weight-bold mt-1 mb-1">
                            <a href={url} onClick={(e) => fireClick(e, url)}>
                              {brandName}
                            </a>
                          </b>
                        </div>
                      </div>
                      <div className="mt-1">
                        <div className="lh-1">
                          <b className="mt-2 fw-normal text-xxs text-black">{p.name}</b>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Video format */}
      {hasVideo && (
        <div
          className="position-relative rounded overflow-hidden mb-2 mt-3"
          style={{ cursor: "pointer", background: "#111", minHeight: 160 }}
          onClick={(e) => fireClick(e, defaultLanding)}
        >
          <img
            src={resolveImage(thumbnailUrl)}
            alt={headline}
            className="w-100 object-fit-cover"
            style={{ maxHeight: 200, opacity: 0.85 }}
            onError={(e) => {
              e.currentTarget.src = PLACEHOLDER_IMAGE;
            }}
          />
          <div
            className="position-absolute top-50 start-50 translate-middle"
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="bi bi-play-fill text-dark" style={{ fontSize: 24 }} />
          </div>
        </div>
      )}

      {/* Fallback hero when only custom image is set */}
      {hasHeroOnly && isValidImageUrl(customImage) && (
        <div
          className="rounded overflow-hidden mt-3"
          style={{ cursor: "pointer" }}
          onClick={(e) => fireClick(e, defaultLanding)}
        >
          <img
            src={customImage}
            alt={headline}
            className="w-100 object-fit-cover"
            style={{ maxHeight: 200 }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}

      {sponsoredLabel}
      <hr className="horizontal dark mb-4" />
    </div>
  );
};

export default SponsoredBrandSection;
